import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { calculateVinylGeometry } from '../utils/vinylGeometry';

interface VinylOverlayProps {
  /** Height of the visible slide area this vinyl sits under (px). */
  slideHeight: number;
  /** Radius of the carousel path (px). */
  carouselRadius: number;
  /** Base gradient for the vinyl body. */
  background?: string;
  /** Spin speed in RPM. Typical: 33.33, 45, 78. */
  rpm?: number;
  /** Enable/disable rotation. */
  rotate?: boolean;
  /** Tilt in degrees for the perspective effect (rotateX). */
  tiltDeg?: number;
  /** Directional light angle in degrees for the specular highlight. */
  lightAngleDeg?: number;
  /** 0..1: how shiny the record looks. */
  shineIntensity?: number;
  /** Groove density in pixels per cycle (smaller = tighter grooves). */
  grooveDensityPx?: number;
  /** 0..1: strength of ordered surface texture lines. */
  textureIntensity?: number;
  /** 0..1: strength of realistic scratches. */
  scratchIntensity?: number;
  /** Outer rim width in px (pressed edge). */
  rimWidthPx?: number;
  /** Label settings */
  labelColor?: string;
  labelImageUrl?: string;
  /** Label radius as fraction of disk radius (0..1). Typical ~0.32 */
  labelRadiusRatio?: number;
  /** Center hole radius as fraction of disk radius */
  centerHoleRadiusRatio?: number;
  /** Optional color tint overlay for transitions. */
  tintColor?: string;
  /** 0..1: opacity of the tint overlay. */
  tintOpacity?: number;
}

const DEFAULT_BG =
  'radial-gradient(circle at 52% 48%, #161616 0%, #0b0b0b 60%, #000 100%)';

const VinylOverlay: React.FC<VinylOverlayProps> = ({
  slideHeight,
  carouselRadius,
  background = DEFAULT_BG,
  rpm = 33.33,
  rotate = true,
  tiltDeg = 65,
  lightAngleDeg = 120,
  shineIntensity = 1.0,
  grooveDensityPx = 25.1,
  textureIntensity = 1.0,
  scratchIntensity = 0.1,
  rimWidthPx = 50,
  labelColor = '#d9d5c9',
  labelImageUrl,
  labelRadiusRatio = 0.16,
  centerHoleRadiusRatio = 0.018,
  tintColor = '#ff4fd8',
  tintOpacity = 0,
}) => {
  if (slideHeight <= 0 || carouselRadius <= 0) return null;

  const { radius } = calculateVinylGeometry(slideHeight, carouselRadius);
  const diameter = radius * 2;

  const spinSeconds = Math.max(0.001, 60 / Math.max(1e-3, rpm));
  // (ratio * radius) * 2 to compute diameter
  const labelDiameter = Math.max(0, Math.min(1, labelRadiusRatio)) * diameter * 2;
  const holeDiameter = Math.max(0, Math.min(1, centerHoleRadiusRatio)) * diameter * 2;

  const grooves = useMemo(() => {
    // Thin alternating rings to mimic grooves. Slight alpha to avoid moiré.
    const lightA = 0.005 * Math.min(1, Math.max(0, shineIntensity));
    const darkA = 0.05 + 0.2 * Math.min(1, Math.max(0, shineIntensity));
    const g = Math.max(0.5, grooveDensityPx);
    return `repeating-radial-gradient(circle at center,
      rgba(255,255,255,${lightA}) 0px,
      rgba(255,255,255,${lightA}) ${g * 0.5}px,
      rgba(0,0,0,${darkA}) ${g * 0.5}px,
      rgba(0,0,0,${darkA}) ${g}px
    )`;
  }, [grooveDensityPx, shineIntensity]);

  const surfaceTexture = useMemo(() => {
    // Faint crosshatch texture so rotation is visible.
    const s = Math.min(1, Math.max(0, textureIntensity));
    const a = 0.02 * s;
    return `repeating-linear-gradient(45deg,
        rgba(255,255,255,0) 0px,
        rgba(255,255,255,0) 2px,
        rgba(255,255,255,${a}) 3px,
        rgba(255,255,255,0) 4px
      ),
      repeating-linear-gradient(-45deg,
        rgba(0,0,0,0) 0px,
        rgba(0,0,0,0) 2px,
        rgba(0,0,0,${a}) 3px,
        rgba(0,0,0,0) 4px
      )`;
  }, [textureIntensity]);

  const scratches = useMemo(() => {
    // Irregular scratches that catch light for depth.
    const s = Math.min(1, Math.max(0, scratchIntensity));
    if (s <= 0) return 'none';
    const angle = lightAngleDeg + 90;
    const hi = 0.3 * s;
    const lo = 0.3 * s;
    const layers = [13, 17, 19].map((gap, i) => {
      const offset = i * 2;
      return `repeating-linear-gradient(${angle}deg,
        rgba(255,255,255,${hi}) ${offset}px,
        rgba(255,255,255,${hi}) ${offset + 1}px,
        rgba(0,0,0,${lo}) ${offset + 1}px,
        rgba(0,0,0,${lo}) ${offset + 2}px,
        transparent ${offset + 2}px,
        transparent ${offset + gap}px
      )`;
    });
    return layers.join(', ');
  }, [scratchIntensity, lightAngleDeg]);

  const rim = useMemo(() => {
    // Subtle pressed outer edge.
    return `radial-gradient(circle at center,
      transparent calc(100% - ${rimWidthPx}px),
      rgba(0,0,0,0.9) calc(100% - ${Math.max(1, rimWidthPx - 1)}px),
      rgba(0,0,0,1) 100%
    )`;
  }, [rimWidthPx]);

  const shine = useMemo(() => {
    // Conic + radial blend for specular highlight. Kept visually fixed via counter-rotation.
    const a = lightAngleDeg;
    const s = Math.min(1, Math.max(0, shineIntensity));
    const conic = `conic-gradient(from ${a}deg at 50% 50%,
      rgba(255,255,255,0) 0deg,
      rgba(255,255,255,${0.22 * s}) 8deg,
      rgba(255,255,255,0) 24deg,
      rgba(255,255,255,0) 360deg
    )`;
    const radial = `radial-gradient( circle at 62% 30%,
      rgba(255,255,255,${0.18 * s}) 0%,
      rgba(255,255,255,0) 50%
    )`;
    return `${conic}, ${radial}`;
  }, [lightAngleDeg, shineIntensity]);

  const tiltRange = 0.2;
  const tiltMin = tiltDeg - tiltRange;
  const tiltMax = tiltDeg + tiltRange;

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    zIndex: -1,
    top: '50%',
    left: '50%',
    width: `${diameter}px`,
    height: `${diameter}px`,
    transform: `translate(-50%, -50%) rotateX(${tiltDeg}deg) translateY(${slideHeight}px)`,
    transformOrigin: 'center center',
    willChange: 'transform',
    pointerEvents: 'none',
    animation: rotate ? `rv-tilt ${spinSeconds}s ease-in-out infinite` : undefined,
    animationPlayState: rotate ? 'running' : 'paused',
    maskImage: 'linear-gradient(to top, rgba(0,0,0,1), rgba(0,0,0,0))',
    WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,1), rgba(0,0,0,0))',
    filter: 'drop-shadow(0 24px 40px rgba(0,0,0,0.55))',
  } as const;

  const diskStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    borderRadius: '60%',
    backgroundImage: `${background}, ${grooves}, ${rim}`,
    backgroundBlendMode: 'overlay, overlay, overlay',
    willChange: 'transform',
    transform: 'rotate(0deg)',
    animation: rotate ? `rv-spin ${spinSeconds/2}s linear infinite` : undefined,
    animationPlayState: rotate ? 'running' : 'paused',
    boxShadow:
      'inset 0 0 60px rgba(0,0,0,0.5), inset 0 12px 24px rgba(255,255,255,0.05)',
  } as const;

  const textureStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    backgroundImage: `${surfaceTexture}, ${scratches}`,
    backgroundBlendMode: 'overlay, overlay',
    //mixBlendMode: 'overlay',
    willChange: 'transform',
    transform: 'rotate(0deg)',
    animation: rotate ? `rv-spin ${spinSeconds/100}s linear infinite` : undefined,
    animationPlayState: rotate ? 'running' : 'paused',
    pointerEvents: 'none',
  } as const;

  const tintStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    borderRadius: '60%',
    backgroundColor: tintColor,
    mixBlendMode: 'screen',
    opacity: Math.min(1, Math.max(0, tintOpacity)),
    transition: 'opacity 0.8s ease-in-out',
    pointerEvents: 'none',
  } as const;

  const labelStyle: React.CSSProperties = {
    position: 'absolute',
    top: `calc(50% - ${labelDiameter/2}px)`,
    left: `calc(50% - ${labelDiameter/2}px)`,
    width: `${labelDiameter-10}px`,
    height: `${labelDiameter}px`,
    transform: 'translate(-50%, -50%) rotate(0deg)',
    borderRadius: '50%',
    overflow: 'hidden',
    backgroundColor: labelColor,
    backgroundImage: labelImageUrl ? `url(${labelImageUrl})` : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    boxShadow:
      'inset 0 0 0 2px rgba(0,0,0,0.2), inset 0 8px 16px rgba(0,0,0,0.25)',
    willChange: 'transform',
    animation: rotate ? `rv-spin ${spinSeconds}s linear infinite` : undefined,
    animationPlayState: rotate ? 'running' : 'paused',
  } as const;

  const holeStyle: React.CSSProperties = {
    position: 'absolute',
    top: `calc(50% - ${holeDiameter/2}px)`,
    left: `calc(50% - ${holeDiameter/2}px)`,
    width: `${holeDiameter-2}px`,
    height: `${holeDiameter}px`,
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    background:
      'radial-gradient(circle at 55% 45%, #0a0a0a 0%, #1a1a1a 55%, #000 100%)',
    boxShadow: 'inset 0 0 6px rgba(255,255,255,0.05)',

    animation: rotate ? `rv-spin ${spinSeconds}s linear infinite` : undefined,
    animationPlayState: rotate ? 'running' : 'paused',
  } as const;

  const shineStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    backgroundImage: shine,
    mixBlendMode: 'screen',
    opacity: Math.min(1, Math.max(0, 0.75 * shineIntensity)),
    filter: 'blur(0.4px)',
    pointerEvents: 'none',
  } as const;

  const styleTag = (
    <style>
      {`
      @keyframes rv-spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
      @keyframes rv-tilt {
        0% { transform: translate(-50%, -50%) rotateX(${tiltMin}deg) translateY(${slideHeight}px); }
        50% { transform: translate(-50%, -50%) rotateX(${tiltMax}deg) translateY(${slideHeight}px); }
        100% { transform: translate(-50%, -50%) rotateX(${tiltMin}deg) translateY(${slideHeight}px); }
      }
    `}
    </style>
  );

  return (
    <motion.div
      style={containerStyle}
      aria-hidden
      layoutId="vinyl-disk"
      transition={{ duration: 0.8, ease: 'easeInOut' }}
    >
      {styleTag}
      <div style={diskStyle} />
      <div style={textureStyle} />
      <div style={tintStyle} />
      <div style={shineStyle} />
      <div style={labelStyle} />
      <div style={holeStyle} />
    </motion.div>
  );
};

export default VinylOverlay;

